import type {
  Capabilities,
  Channel,
  WidgetDelivery,
  WidgetManifest,
  WidgetProvider,
  WidgetResolution,
} from '@resto/contracts';
import { REGISTRY_DELIVERIES } from '@resto/contracts';

export type DeliveryPolicy = Partial<Record<Channel, WidgetDelivery>>;

export type WidgetDeliveryErrorCode =
  | 'not_bundled'
  | 'not_allowed'
  | 'invalid_manifest'
  | 'load_failed';

export class WidgetDeliveryError extends Error {
  readonly code: WidgetDeliveryErrorCode;

  constructor(code: WidgetDeliveryErrorCode, message: string) {
    super(message);
    this.name = 'WidgetDeliveryError';
    this.code = code;
  }
}

/** remote: страница и модуль лежат на CDN, хост только открывает entryUrl. */
/**
 * Проверка целостности бандла: sha256 модуля должен совпасть с манифестом.
 * Несовпадение — отказ от запуска, а не «загрузим как есть».
 */
export async function verifyChecksum(url: string, expected: string): Promise<void> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return;
  let response: Response;
  try {
    response = await fetch(url, { cache: 'no-store' });
  } catch (error) {
    throw new WidgetDeliveryError('load_failed', `не удалось скачать модуль ${url}: ${String(error)}`);
  }
  if (!response.ok) {
    throw new WidgetDeliveryError('load_failed', `модуль ${url} вернул ${response.status}`);
  }
  const digest = await crypto.subtle.digest('SHA-256', await response.arrayBuffer());
  const actual = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  if (actual !== expected.toLowerCase()) {
    throw new WidgetDeliveryError(
      'load_failed',
      `контрольная сумма виджета не совпала: ожидалось ${expected}, получено ${actual}`,
    );
  }
}

export class RemoteWidgetProvider implements WidgetProvider {
  readonly kind = 'remote' as const;
  readonly #allowlist: readonly string[];

  constructor(options: { allowlist?: readonly string[] } = {}) {
    this.#allowlist = options.allowlist ?? [];
  }

  async resolve(manifest: WidgetManifest): Promise<WidgetResolution> {
    if (!manifest.entryUrl) {
      throw new WidgetDeliveryError('invalid_manifest', `у виджета ${manifest.id} нет entryUrl`);
    }
    this.#assertAllowed(manifest.entryUrl);
    if (manifest.moduleUrl) this.#assertAllowed(manifest.moduleUrl);
    if (manifest.checksum && manifest.moduleUrl) {
      await verifyChecksum(manifest.moduleUrl, manifest.checksum);
    }
    return {
      kind: 'remote',
      htmlUrl: manifest.entryUrl,
      moduleUrl: manifest.moduleUrl,
      version: manifest.version,
      fallback: false,
    };
  }

  #assertAllowed(url: string): void {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      throw new WidgetDeliveryError('not_allowed', `некорректный адрес виджета: ${url}`);
    }
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      throw new WidgetDeliveryError('not_allowed', `протокол не разрешён: ${parsed.protocol}`);
    }
    if (this.#allowlist.length === 0) return;
    const allowed = this.#allowlist.some(
      (host) => parsed.hostname === host || parsed.hostname.endsWith(`.${host}`),
    );
    if (!allowed) {
      throw new WidgetDeliveryError('not_allowed', `домен не в allow-list: ${parsed.hostname}`);
    }
  }
}

export interface BundledEntry {
  htmlUrl: string;
  moduleUrl: string;
  version: string;
}

export type BundledRegistry = Readonly<Record<string, BundledEntry>>;

/**
 * bundled: код виджета собран в приложение, статический реестр сгенерирован
 * на этапе сборки (scripts/build-widgets.mjs). Сеть не нужна.
 */
export class BundledWidgetProvider implements WidgetProvider {
  readonly kind = 'bundled' as const;

  constructor(private readonly registry: BundledRegistry) {}

  async resolve(manifest: WidgetManifest): Promise<WidgetResolution> {
    const entry = this.registry[manifest.id];
    if (!entry) {
      throw new WidgetDeliveryError('not_bundled', `виджет ${manifest.id} не собран в приложение`);
    }
    return {
      kind: 'bundled',
      htmlUrl: entry.htmlUrl,
      moduleUrl: entry.moduleUrl,
      version: entry.version,
      fallback: false,
    };
  }
}

export interface ResolveWidgetContext {
  channel: Channel;
  hostVersion: string;
  capabilities: Capabilities;
  /** Переопределение режима поставки на уровне канала (конфиг хоста). */
  deliveries?: DeliveryPolicy;
  providers: {
    remote: WidgetProvider;
    bundled?: WidgetProvider;
  };
}

export function deliveryForChannel(
  manifest: WidgetManifest,
  channel: Channel,
  policy?: DeliveryPolicy,
): WidgetDelivery {
  return policy?.[channel] ?? REGISTRY_DELIVERIES[channel] ?? manifest.delivery;
}

/**
 * Главная точка выбора провайдера. При недоступном remote и наличии
 * bundled-сборки — откат на неё, без правок кода виджета.
 */
export async function resolveWidget(
  manifest: WidgetManifest,
  context: ResolveWidgetContext,
): Promise<WidgetResolution> {
  const delivery = deliveryForChannel(manifest, context.channel, context.deliveries);
  const primary = delivery === 'bundled' ? context.providers.bundled : context.providers.remote;

  if (primary) {
    try {
      return await primary.resolve(manifest);
    } catch (error) {
      if (delivery === 'remote' && context.providers.bundled) {
        const resolution = await context.providers.bundled.resolve(manifest);
        return { ...resolution, fallback: true };
      }
      throw error;
    }
  }

  if (delivery === 'bundled' && context.providers.bundled) {
    const resolution = await context.providers.bundled.resolve(manifest);
    return { ...resolution, fallback: false };
  }

  throw new WidgetDeliveryError(
    'not_bundled',
    `для канала ${context.channel} нет доступного провайдера виджета ${manifest.id}`,
  );
}
